package com.karyadina.ui.screens

import androidx.compose.animation.*
import androidx.compose.animation.core.*
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.ExperimentalFoundationApi
import androidx.compose.foundation.clickable
import androidx.compose.foundation.combinedClickable
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.automirrored.filled.ArrowForward
import androidx.compose.material.icons.filled.*
import androidx.compose.material.icons.outlined.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.lifecycle.viewmodel.compose.viewModel
import androidx.lifecycle.viewmodel.initializer
import androidx.lifecycle.viewmodel.viewModelFactory
import com.karyadina.model.*
import com.karyadina.model.Unit as StudyUnit
import com.karyadina.service.PomodoroServiceImpl
import com.karyadina.ui.screens.pomodorotimer.presentation.PromodorTimerEvent
import com.karyadina.ui.screens.pomodorotimer.presentation.PromodorTimerViewModel
import com.karyadina.ui.theme.StudyStrings
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun PomodoroTimerScreen(
    service: PomodoroServiceImpl,
    onBackClick: () -> kotlin.Unit,
    onHistoryClick: () -> Unit,
    modifier: Modifier = Modifier,
    viewModel: PromodorTimerViewModel = remember { PromodorTimerViewModel(service) }
) {

    val state by viewModel.uiState.collectAsState()

    var currentSession by remember { mutableStateOf<PomodoroSession?>(null) }
    var timerState = state.timerState // by remember { mutableStateOf(TimerState.IDLE) }
    var remainingSeconds = state.remainingSeconds // by remember { mutableStateOf(0) }
    var selectedSessionType = state.selectedType // by remember { mutableStateOf(SessionType.FOCUS) }
    var taskName: String  by remember { mutableStateOf("") }
    // stats by remember { mutableStateOf(service.getStatistics()) } // Removed local state
    val stats = state.stats ?: service.getStatistics() // Use state stats
    // var config by remember { mutableStateOf(service.getConfig()) } // Removed local state
    val config = state.config ?: service.getConfig() // Use state config
    var showSettings by remember { mutableStateOf(false) }
    var recentActivityExpanded by remember { mutableStateOf(false) }
    
    // Calming, stable background color based on session type - Using Theme Containers for dark mode support
    val targetColor = when (selectedSessionType) {
        SessionType.FOCUS -> MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.2f)
        SessionType.SHORT_BREAK -> MaterialTheme.colorScheme.secondaryContainer.copy(alpha = 0.2f)
        SessionType.LONG_BREAK -> MaterialTheme.colorScheme.tertiaryContainer.copy(alpha = 0.2f)
    }
    
    val animatedBackgroundColor by animateColorAsState(
        targetValue = targetColor,
        animationSpec = tween(durationMillis = 1000),
        label = "background_color"
    )
    
    val scope = rememberCoroutineScope()

   /* fun startSession() {
        currentSession = service.startSession(selectedSessionType, taskName.ifBlank { null })
        remainingSeconds = service.getRemainingTime(currentSession!!.id)
        timerState = TimerState.RUNNING
    }*/
    
    // Timer countdown effect


    fun pauseSession() {
        currentSession?.let {
            service.pauseSession(it.id)
            timerState = TimerState.PAUSED
        }
    }

    fun resumeSession() {
        currentSession?.let {
            service.resumeSession(it.id)
            timerState = TimerState.RUNNING
        }
    }

    fun stopSession() {
        currentSession?.let {
            service.cancelSession(it.id)
            timerState = TimerState.IDLE
            currentSession = null
            // stats = service.getStatistics() // REMOVED: Now handled by state
        }
    }

    // Focus Mode - hide everything when timer is running
    val isFocusMode = timerState == TimerState.RUNNING

    Scaffold(
        topBar = {
            AnimatedVisibility(
                visible = !isFocusMode,
                enter = slideInVertically() + fadeIn(),
                exit = slideOutVertically() + fadeOut()
            ) {
                TopAppBar(
                    title = {
                        Column {
                            Text(stringResource(StudyStrings.TimerTitle))
                            if (stats.todayFocusTime > 0) {
                                Text(
                                    stringResource(StudyStrings.TimerFocusedToday, stats.todayFocusTime),
                                    style = MaterialTheme.typography.labelSmall,
                                    color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f)
                                )
                            }
                        }
                    },
                    navigationIcon = {
                        IconButton(onClick = onBackClick) {
                            Icon(Icons.AutoMirrored.Filled.ArrowBack, stringResource(StudyStrings.Back))
                        }
                    },
                    actions = {
                        BadgedBox(
                            badge = {
                                Badge(
                                    containerColor = MaterialTheme.colorScheme.primary
                                ) {
                                    Text(
                                        "${service.getSessionHistory().size}",
                                        style = MaterialTheme.typography.labelSmall
                                    )
                                }
                            }
                        ) {
                            IconButton(onClick = onHistoryClick) {
                                Icon(Icons.Outlined.History, stringResource(StudyStrings.TimerHistory))
                            }
                        }
                        IconButton(onClick = { showSettings = !showSettings }) {
                            Icon(Icons.Outlined.Settings, stringResource(StudyStrings.TimerSettings))
                        }
                    }
                )
            }
        }
    ) { padding ->
        Box(
            modifier = modifier
                .fillMaxSize()
                .then(if (isFocusMode) Modifier else Modifier.padding(padding))
                .background(
                    Brush.verticalGradient(
                        colors = listOf(
                            MaterialTheme.colorScheme.surface,
                            animatedBackgroundColor
                        )
                    )
                )
        ) {
            if (isFocusMode) {
                // Focus Mode - Calming, zen-like fullscreen experience
                
                // Zen Mode State (Auto-Hide Controls)
                var zenMode by remember { mutableStateOf(false) }
                var lastInteractionTime by remember { mutableStateOf(0L) } // Timestamp
                
                // Mantra Words
                val mantras = listOf("Focus", "Breathe", "Flow", "Shanti", "Ekagrata")
                var mantraIndex by remember { mutableStateOf(0) }
                
                // Effect: Auto-enable Zen Mode after 5s of inactivity
                LaunchedEffect(lastInteractionTime) {
                    delay(5000)
                    zenMode = true
                }
                
                // Effect: Rotate Mantra every 10s
                LaunchedEffect(Unit) {
                    while(true) {
                        delay(10000)
                        mantraIndex = (mantraIndex + 1) % mantras.size
                    }
                }
                
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .pointerInput(Unit) {
                            detectTapGestures(
                                onTap = { 
                                    zenMode = false
                                    lastInteractionTime = com.karyadina.util.currentTimeMillis()
                                }
                            )
                        },
                    contentAlignment = Alignment.Center
                ) {
                    Column(
                        modifier = Modifier
                            .fillMaxSize()
                            .padding(32.dp),
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.Center
                    ) {
                        // Motivational message / Zen Mantra
                        // In Zen Mode, show simple rotating Mantra. In Active Focus, show "Stay Focused" + Icon
                        
                        AnimatedVisibility(
                            visible = !zenMode,
                            enter = fadeIn(),
                            exit = fadeOut()
                        ) {
                            Column(horizontalAlignment = Alignment.CenterHorizontally) {
                                Icon(
                                    when (selectedSessionType) {
                                        SessionType.FOCUS -> Icons.Filled.Psychology
                                        SessionType.SHORT_BREAK -> Icons.Filled.Coffee
                                        SessionType.LONG_BREAK -> Icons.Filled.SelfImprovement
                                    },
                                    contentDescription = null,
                                    modifier = Modifier.size(32.dp),
                                    tint = MaterialTheme.colorScheme.primary.copy(alpha = 0.6f)
                                )
                                Spacer(modifier = Modifier.height(12.dp))
                                Text(
                                    when (selectedSessionType) {
                                        SessionType.FOCUS -> stringResource(StudyStrings.TimerMantraFocus)
                                        SessionType.SHORT_BREAK -> stringResource(StudyStrings.TimerMantraShortBreak)
                                        SessionType.LONG_BREAK -> stringResource(StudyStrings.TimerMantraLongBreak)
                                    },
                                    style = MaterialTheme.typography.titleMedium,
                                    fontWeight = FontWeight.Light,
                                    color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f)
                                )
                            }
                        }
                        
                         // Zen Mantra (Visible only in Zen Mode, effectively replaces the top block)
                        AnimatedVisibility(
                             visible = zenMode && selectedSessionType == SessionType.FOCUS,
                             enter = fadeIn(tween(1000)),
                             exit = fadeOut()
                        ) {
                             Text(
                                mantras[mantraIndex],
                                style = MaterialTheme.typography.headlineMedium,
                                fontWeight = FontWeight.ExtraLight,
                                color = MaterialTheme.colorScheme.primary.copy(alpha = 0.5f),
                                letterSpacing = 4.sp
                            )
                        }

                        
                        Spacer(modifier = Modifier.height(48.dp))
                        
                        // Large Timer Display with breathing effect
                        val scale by animateFloatAsState(
                            targetValue = if (state.remainingSeconds % 2 == 0) 1.0f else 1.02f,
                            animationSpec = tween(durationMillis = 1000, easing = FastOutSlowInEasing),
                            label = "breathing_effect"
                        )
                        
                        Box(
                            modifier = Modifier.graphicsLayer {
                                scaleX = scale
                                scaleY = scale
                            }
                        ) {
                            ChakraTimer( // Use new Chakra Timer
                                remainingSeconds = state.remainingSeconds,
                                totalSeconds = state.totalSeconds,
                                sessionType = state.selectedType,
                                timerState = state.timerState,
                                onTap = { viewModel.onEvent(PromodorTimerEvent.PauseSession) }
                            )
                        }
                        
                        Spacer(modifier = Modifier.height(48.dp))
                        
                        // Task name with fade animation
                        // Hide in Zen Mode
                        AnimatedVisibility(
                            visible = state.activeTaskName != null && !zenMode, // Hide in Zen
                            enter = fadeIn() + slideInVertically(),
                            exit = fadeOut()
                        ) {
                            Column(horizontalAlignment = Alignment.CenterHorizontally) {
                                Text(
                                    stringResource(StudyStrings.TimerLabelWorkingOn),
                                    style = MaterialTheme.typography.labelMedium,
                                    color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.5f)
                                )
                                Spacer(modifier = Modifier.height(8.dp))
                                Text(
                                    state.activeTaskName ?: "",
                                    style = MaterialTheme.typography.headlineMedium,
                                    fontWeight = FontWeight.Medium,
                                    color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.8f),
                                    textAlign = androidx.compose.ui.text.style.TextAlign.Center
                                )
                            }
                        }
                    }
                    
                    // Subtle stop button at bottom
                    // Hide in Zen Mode
                    AnimatedVisibility(
                        visible = !zenMode,
                        enter = fadeIn(),
                        exit = fadeOut(),
                        modifier = Modifier
                            .align(Alignment.BottomCenter)
                            .padding(bottom = 32.dp)
                    ) {
                        Box {
                            TextButton(
                                onClick = { viewModel.onEvent(PromodorTimerEvent.StopSession) },
                                colors = ButtonDefaults.textButtonColors(
                                    contentColor = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.4f)
                                )
                            ) {
                                Icon(
                                    Icons.Filled.Stop,
                                    contentDescription = "Stop",
                                    modifier = Modifier.size(18.dp)
                                )
                                Spacer(modifier = Modifier.width(6.dp))
                                Text(
                                    stringResource(StudyStrings.TimerBtnEnd),
                                    style = MaterialTheme.typography.labelMedium,
                                    fontWeight = FontWeight.Light
                                )
                            }
                        }
                    }
                }
            } else {
                // Normal Mode - Full interface
                LazyColumn(
                    modifier = Modifier
                        .fillMaxSize()
                        .padding(horizontal = 24.dp),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.spacedBy(24.dp),
                    contentPadding = PaddingValues(vertical = 24.dp)
                ) {
                    // Statistics Cards
                    item {
                        StatsRow(stats)
                    }
                    
                    // Timer Circle
                    item {
                        ChakraTimer( // Use new Chakra Timer
                            remainingSeconds = state.remainingSeconds,
                            totalSeconds = state.totalSeconds,
                            sessionType = state.selectedType,
                            timerState = state.timerState,
                            onTap = {
                                when (state.timerState) {
                                    TimerState.IDLE, TimerState.COMPLETED -> viewModel.onEvent(
                                        PromodorTimerEvent.StartSession(
                                            taskName
                                        )
                                    )
                                    TimerState.RUNNING -> viewModel.onEvent(PromodorTimerEvent.PauseSession)
                                    TimerState.PAUSED -> viewModel.onEvent(PromodorTimerEvent.ResumeSession)
                                }
                            }
                        )
                    }
                
                // Session Type Selector
                item {
                    if (state.timerState == TimerState.IDLE || state.timerState == TimerState.COMPLETED) {
                        SessionTypeSelector(
                            selectedType = state.selectedType,
                            onTypeSelected = { newType ->
                                viewModel.onEvent(PromodorTimerEvent.SelectSessionType(newType))
                            }
                        )
                    }
                }
                
                // Task Input
                item {
                    if (timerState == TimerState.IDLE && selectedSessionType == SessionType.FOCUS) {
                        OutlinedTextField(
                            value = state.taskInput,
                            onValueChange = { // send event
                                    viewModel.onEvent(PromodorTimerEvent.UpdateTaskInput(it))
                                 },
                            label = { Text(stringResource(StudyStrings.TimerInputTask)) },
                            modifier = Modifier.fillMaxWidth(),
                            singleLine = true,
                            leadingIcon = {
                                Icon(Icons.Outlined.Task, null)
                            }
                        )
                    } else if (timerState != TimerState.IDLE && state.activeTaskName != null) {
                        Text(
                            "Working on: ${state.activeTaskName}",
                            style = MaterialTheme.typography.bodyMedium,
                            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f)
                        )
                    }
                }
                
                // Stop Button (only show when running or paused)
                if (timerState == TimerState.RUNNING || timerState == TimerState.PAUSED) {
                    item {
                        Spacer(modifier = Modifier.height(16.dp))
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.Center
                        ) {
                            OutlinedButton(
                                onClick = { viewModel.onEvent(PromodorTimerEvent.StopSession) },
                                colors = ButtonDefaults.outlinedButtonColors(
                                    contentColor = MaterialTheme.colorScheme.error
                                )
                            ) {
                                Icon(
                                    Icons.Filled.Stop,
                                    contentDescription = "Stop Session",
                                    modifier = Modifier.size(18.dp)
                                )
                                Spacer(modifier = Modifier.width(8.dp))
                                Text(stringResource(StudyStrings.TimerBtnStop))
                            }
                        }
                        Spacer(modifier = Modifier.height(24.dp))
                    }
                }
                
                // Recent Activity Section (Collapsible)
                item {
                    // Use remember with stats as key to trigger recomposition when history changes
                    val recentSessions = remember(stats) { service.getSessionHistory().take(3) }
                    if (recentSessions.isNotEmpty()) {
                        Column(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(horizontal = 16.dp)
                        ) {
                            // Header with expand/collapse
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .clickable { recentActivityExpanded = !recentActivityExpanded }
                                    .padding(vertical = 8.dp),
                                horizontalArrangement = Arrangement.SpaceBetween,
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Row(
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Icon(
                                        Icons.Filled.History,
                                        contentDescription = null,
                                        modifier = Modifier.size(20.dp),
                                        tint = MaterialTheme.colorScheme.primary
                                    )
                                    Spacer(modifier = Modifier.width(8.dp))
                                    Text(
                                        stringResource(StudyStrings.TimerRecentActivity),
                                        style = MaterialTheme.typography.titleMedium,
                                        fontWeight = FontWeight.Bold
                                    )
                                }
                                
                                Icon(
                                    if (recentActivityExpanded) Icons.Filled.ExpandLess else Icons.Filled.ExpandMore,
                                    contentDescription = if (recentActivityExpanded) "Collapse" else "Expand",
                                    tint = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f)
                                )
                            }
                            
                            // Expandable content
                            AnimatedVisibility(
                                visible = recentActivityExpanded,
                                enter = expandVertically(animationSpec = spring()) + fadeIn(),
                                exit = shrinkVertically(animationSpec = spring()) + fadeOut()
                            ) {
                                Column {
                                    recentSessions.forEach { session ->
                                        RecentSessionItem(session = session)
                                    }
                                    
                                    Spacer(modifier = Modifier.height(8.dp))
                                    
                                    TextButton(
                                        onClick = onHistoryClick,
                                        modifier = Modifier.fillMaxWidth()
                                    ) {
                                        Text(stringResource(StudyStrings.TimerViewAllHistory))
                                        Spacer(modifier = Modifier.width(4.dp))
                                        Icon(
                                            Icons.AutoMirrored.Filled.ArrowForward,
                                            contentDescription = null,
                                            modifier = Modifier.size(16.dp)
                                        )
                                    }
                                }
                            }
                        }
                    }
                }
                }
            }
            
            // Settings Dialog (always available)
            if (showSettings) {
                SettingsDialog(
                    config = config,
                    viewModel = viewModel,  // Pass ViewModel
                    onConfigChange = { /* handled via viewModel */ },
                    onDismiss = { showSettings = false }
                )
            }
        }
    }
}

@Composable
fun StatsRow(stats: PomodoroStats) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        StatCard(
            title = stringResource(StudyStrings.TimerStatsToday),
            value = "${stats.todayFocusTime}m",
            icon = Icons.Filled.Today,
            modifier = Modifier.weight(1f)
        )
        StatCard(
            title = stringResource(StudyStrings.TimerStatsWeek),
            value = "${stats.weeklyFocusTime}m",
            icon = Icons.Filled.CalendarMonth,
            modifier = Modifier.weight(1f)
        )
        StatCard(
            title = stringResource(StudyStrings.TimerStatsStreak),
            value = "${stats.currentStreak}",
            icon = Icons.Filled.LocalFireDepartment,
            modifier = Modifier.weight(1f)
        )
    }
}

@Composable
fun StatCard(
    title: String,
    value: String,
    icon: androidx.compose.ui.graphics.vector.ImageVector,
    modifier: Modifier = Modifier
) {
    ElevatedCard(
        modifier = modifier,
        elevation = CardDefaults.elevatedCardElevation(defaultElevation = 4.dp)
    ) {
        Box {
            // Accent gradient on left side
            Surface(
                modifier = Modifier
                    .width(4.dp)
                    .height(80.dp)
                    .align(Alignment.CenterStart),
                color = MaterialTheme.colorScheme.primary,
                shape = MaterialTheme.shapes.small
            ) {}
            
            Column(
                modifier = Modifier
                    .padding(start = 16.dp, top = 12.dp, end = 12.dp, bottom = 12.dp)
                    .fillMaxWidth(),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Icon(
                    icon,
                    contentDescription = null,
                    modifier = Modifier.size(24.dp),
                    tint = MaterialTheme.colorScheme.primary
                )
                Spacer(modifier = Modifier.height(4.dp))
                Text(
                    value,
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.Bold
                )
                Text(
                    title,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f)
                )
            }
        }
    }
}

@OptIn(ExperimentalFoundationApi::class)
@Composable
fun ChakraTimer(
    remainingSeconds: Int,
    totalSeconds: Int,
    sessionType: SessionType,
    timerState: TimerState,
    onTap: () -> kotlin.Unit
) {
    val progress = if (totalSeconds > 0) remainingSeconds.toFloat() / totalSeconds else 0f
    
    // Smooth progress animation
    val animatedProgress by animateFloatAsState(
        targetValue = progress,
        animationSpec = spring(stiffness = Spring.StiffnessLow)
    )
    
    // Theme Colors - Using semantic colors from Theme.kt for dark mode support
    val primaryColor = when (sessionType) {
        SessionType.FOCUS -> MaterialTheme.colorScheme.primary
        SessionType.SHORT_BREAK -> MaterialTheme.colorScheme.secondary
        SessionType.LONG_BREAK -> MaterialTheme.colorScheme.tertiary
    }
    
    // Rotation Animation (The Wheel of Time)
    val infiniteTransition = rememberInfiniteTransition()
    val rotation by infiniteTransition.animateFloat(
        initialValue = 0f,
        targetValue = 360f,
        animationSpec = infiniteRepeatable(
            animation = tween(durationMillis = if (timerState == TimerState.RUNNING) 20000 else 60000, easing = LinearEasing)
        )
    )
    
    // Breathing/Pulse Animation (Pranayama)
    val pulseScale by infiniteTransition.animateFloat(
        initialValue = 1f,
        targetValue = 1.05f,
        animationSpec = infiniteRepeatable(
            animation = tween(4000, easing = FastOutSlowInEasing), // 4s in-breath
            repeatMode = RepeatMode.Reverse
        )
    )

    Box(
        modifier = Modifier
            .size(320.dp)
            .graphicsLayer {
                scaleX = pulseScale
                scaleY = pulseScale
            },
        contentAlignment = Alignment.Center
    ) {
         // Glow Effect
         Box(
            modifier = Modifier
                .size(320.dp)
                .background(
                    Brush.radialGradient(
                        colors = listOf(primaryColor.copy(alpha = 0.2f), Color.Transparent)
                    ),
                    shape = CircleShape
                )
         )

         // The Main Chakra
         Canvas(
             modifier = Modifier
                 .size(280.dp)
                 .clip(CircleShape)
                 .clickable { onTap() }
                 .graphicsLayer { rotationZ = if (timerState == TimerState.RUNNING || timerState == TimerState.PAUSED) rotation else 0f }
         ) {
             val center = center
             val radius = size.minDimension / 2
             val strokeWidth = 5.dp.toPx()
             
             // 1. Outer Rim (The Universe)
             drawCircle(
                 color = primaryColor,
                 radius = radius,
                 style = androidx.compose.ui.graphics.drawscope.Stroke(width = strokeWidth)
             )
             
             // 2. Inner Rim
             drawCircle(
                 color = primaryColor.copy(alpha = 0.5f),
                 radius = radius * 0.85f,
                 style = androidx.compose.ui.graphics.drawscope.Stroke(width = strokeWidth * 0.5f)
             )
             
             // 3. The 24 Spokes (Ashoka Chakra)
             val spokeCount = 24
             val angleStep = 360f / spokeCount
             
             for (i in 0 until spokeCount) {
                 val angle = i * angleStep
                 val rad = angle * (kotlin.math.PI / 180f)
                 
                 // Spokes go from center to inner rim
                 val endX = center.x + (radius * 0.85f) * kotlin.math.cos(rad).toFloat()
                 val endY = center.y + (radius * 0.85f) * kotlin.math.sin(rad).toFloat()
                 
                 drawLine(
                     color = primaryColor,
                     start = center,
                     end = Offset(endX, endY),
                     strokeWidth = strokeWidth * 0.4f,
                     cap = StrokeCap.Round
                 )
                 
                  // Small decoration at the end of spokes (Dharma)
                 drawCircle(
                     color = primaryColor,
                     radius = strokeWidth * 0.6f,
                     center = Offset(endX, endY)
                 )
             }
             
             // 4. The Hub (Source)
             drawCircle(
                 color = primaryColor.copy(alpha = 0.5f),
                 radius = radius * 0.15f,
                 style = androidx.compose.ui.graphics.drawscope.Fill
             )
         }
         
         // Non-Rotated Overlay: Progress & Time
         // Progress Circle (Time Passed)
         CircularProgressIndicator(
            progress = { animatedProgress },
            modifier = Modifier.size(300.dp),
            color = primaryColor.copy(alpha=0.5f),
            strokeWidth = 8.dp,
            trackColor = primaryColor.copy(alpha=0.1f),
            strokeCap = StrokeCap.Round
         )
         
         // Center Info (Time) - Reduced alpha for better integration in dark mode
         Box(
             modifier = Modifier
                 .size(180.dp)
                 .background(
                     color = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.02f),
                     shape = CircleShape
                 ),
             contentAlignment = Alignment.Center
         ) {
             Column(
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.Center
            ) {
                 Text(
                    formatTime(remainingSeconds),
                    style = MaterialTheme.typography.displayLarge,
                    fontSize = 42.sp,
                    fontWeight = FontWeight.Black,
                    color = MaterialTheme.colorScheme.onSurface
                )
                Text(
                     when (timerState) {
                        TimerState.RUNNING -> stringResource(StudyStrings.TimerStatusFocusing)
                        TimerState.PAUSED -> stringResource(StudyStrings.TimerStatusPaused)
                        else -> stringResource(StudyStrings.TimerStatusReady)
                    },
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold,
                    color = primaryColor
                )
            }
         }
    }
}


@Composable
fun SessionTypeSelector(
    selectedType: SessionType,
    onTypeSelected: (SessionType) -> kotlin.Unit,
) {

    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        SessionType.entries.forEach { type ->
            FilterChip(
                selected = selectedType == type,
                onClick = { (onTypeSelected(type)) },
                label = {
                    Text(
                        when (type) {
                            SessionType.FOCUS -> "Focus"
                            SessionType.SHORT_BREAK -> "Short Break"
                            SessionType.LONG_BREAK -> "Long Break"
                        }
                    )
                },
                modifier = Modifier.weight(1f),
                leadingIcon = if (selectedType == type) {
                    {
                        Icon(
                            Icons.Filled.CheckCircle,
                            contentDescription = null,
                            modifier = Modifier.size(18.dp)
                        )
                    }
                } else null
            )
        }
    }
}

/*@Composable
fun SessionTypeSelector(
    selectedType: SessionType,
    onTypeSelected: (SessionType) -> Unit
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        SessionType.entries.forEach { type ->
            FilterChip(
                selected = selectedType == type,
                onClick = { onTypeSelected(type) },
                label = {
                    Text(
                        when (type) {
                            SessionType.FOCUS -> "Focus"
                            SessionType.SHORT_BREAK -> "Short Break"
                            SessionType.LONG_BREAK -> "Long Break"
                        }
                    )
                },
                modifier = Modifier.weight(1f),
                leadingIcon = if (selectedType == type) {
                    {
                        Icon(
                            Icons.Filled.CheckCircle,
                            contentDescription = null,
                            modifier = Modifier.size(18.dp)
                        )
                    }
                } else null
            )
        }
    }
}*/

@Composable
fun TimerControls(
    timerState: TimerState,
    onStart: () -> Unit,
    onPause: () -> Unit,
    onResume: () -> Unit,
    onStop: () -> Unit
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        when (timerState) {
            TimerState.IDLE, TimerState.COMPLETED -> {
                Button(
                    onClick = onStart,
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(56.dp),
                    colors = ButtonDefaults.buttonColors(
                        containerColor = MaterialTheme.colorScheme.primary
                    )
                ) {
                    Icon(Icons.Filled.PlayArrow, null, modifier = Modifier.size(24.dp))
                    Spacer(modifier = Modifier.width(8.dp))
                    Text("Start", style = MaterialTheme.typography.titleMedium)
                }
            }
            TimerState.RUNNING -> {
                OutlinedButton(
                    onClick = onPause,
                    modifier = Modifier.weight(1f).height(56.dp)
                ) {
                    Icon(Icons.Filled.Pause, null)
                    Spacer(modifier = Modifier.width(8.dp))
                    Text("Pause")
                }
                FilledTonalButton(
                    onClick = onStop,
                    modifier = Modifier.weight(1f).height(56.dp),
                    colors = ButtonDefaults.filledTonalButtonColors(
                        containerColor = MaterialTheme.colorScheme.errorContainer
                    )
                ) {
                    Icon(Icons.Filled.Stop, null)
                    Spacer(modifier = Modifier.width(8.dp))
                    Text("Stop")
                }
            }
            TimerState.PAUSED -> {
                Button(
                    onClick = onResume,
                    modifier = Modifier.weight(1f).height(56.dp)
                ) {
                    Icon(Icons.Filled.PlayArrow, null)
                    Spacer(modifier = Modifier.width(8.dp))
                    Text("Resume")
                }
                FilledTonalButton(
                    onClick = onStop,
                    modifier = Modifier.weight(1f).height(56.dp),
                    colors = ButtonDefaults.filledTonalButtonColors(
                        containerColor = MaterialTheme.colorScheme.errorContainer
                    )
                ) {
                    Icon(Icons.Filled.Stop, null)
                    Spacer(modifier = Modifier.width(8.dp))
                    Text("Stop")
                }
            }
        }
    }
}

@Composable
fun SettingsDialog(
    config: PomodoroConfig,
    viewModel: PromodorTimerViewModel,
    onConfigChange: (PomodoroConfig) -> Unit,
    onDismiss: () -> Unit
) {
    var focusDuration by remember { mutableStateOf(config.focusDuration.toString()) }
    var shortBreak by remember { mutableStateOf(config.shortBreakDuration.toString()) }
    var longBreak by remember { mutableStateOf(config.longBreakDuration.toString()) }
    var autoStartBreaks by remember { mutableStateOf(config.autoStartBreaks) }
    var autoStartPomodoros by remember { mutableStateOf(config.autoStartPomodoros) }
    
    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text("Timer Settings") },
        text = {
            Column(
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                OutlinedTextField(
                    value = focusDuration,
                    onValueChange = { focusDuration = it },
                    label = { Text("Focus Duration (minutes)") },
                    modifier = Modifier.fillMaxWidth()
                )
                OutlinedTextField(
                    value = shortBreak,
                    onValueChange = { shortBreak = it },
                    label = { Text("Short Break (minutes)") },
                    modifier = Modifier.fillMaxWidth()
                )
                OutlinedTextField(
                    value = longBreak,
                    onValueChange = { longBreak = it },
                    label = { Text("Long Break (minutes)") },
                    modifier = Modifier.fillMaxWidth()
                )
                
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text("Auto-start breaks", modifier = Modifier.weight(1f))
                    Switch(checked = autoStartBreaks, onCheckedChange = { autoStartBreaks = it })
                }
                
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text("Auto-start focus", modifier = Modifier.weight(1f))
                    Switch(checked = autoStartPomodoros, onCheckedChange = { autoStartPomodoros = it })
                }
            }
        },
        confirmButton = {
            TextButton(onClick = {
                val newConfig = config.copy(
                    focusDuration = focusDuration.toIntOrNull() ?: 25,
                    shortBreakDuration = shortBreak.toIntOrNull() ?: 5,
                    longBreakDuration = longBreak.toIntOrNull() ?: 15,
                    autoStartBreaks = autoStartBreaks,
                    autoStartPomodoros = autoStartPomodoros
                )
                // Use ViewModel event to update config and refresh UI
                // onConfigChange(newConfig) // Replaced by:
                viewModel.onEvent(PromodorTimerEvent.UpdateConfig(newConfig))
                onDismiss()
            }) {
                Text("Save")
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text("Cancel")
            }
        }
    )
}

fun formatTime(seconds: Int): String {
    val minutes = seconds / 60
    val secs = seconds % 60
    return "${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}"
}

fun generateRandomLightColor(): Color {
    val colors = listOf(
        Color(0xFF6200EE), // Purple
        Color(0xFF03DAC6), // Teal
        Color(0xFF3700B3), // Dark Purple
        Color(0xFF018786), // Dark Teal
        Color(0xFFBB86FC), // Light Purple
        Color(0xFF03A9F4), // Blue
        Color(0xFF4CAF50), // Green
        Color(0xFFFF9800), // Orange
        Color(0xFFE91E63), // Pink
        Color(0xFF9C27B0), // Purple
        Color(0xFF2196F3), // Blue
        Color(0xFF00BCD4), // Cyan
        Color(0xFFFFEB3B), // Yellow
        Color(0xFFFF5722), // Deep Orange
        Color(0xFF673AB7), // Deep Purple
    )
    return colors.random().copy(alpha = 0.2f)
}

fun getNextSessionType(currentType: SessionType, completedSessions: Int): SessionType {
    return when (currentType) {
        SessionType.FOCUS -> {
            if ((completedSessions + 1) % 4 == 0) {
                SessionType.LONG_BREAK
            } else {
                SessionType.SHORT_BREAK
            }
        }
        SessionType.SHORT_BREAK, SessionType.LONG_BREAK -> SessionType.FOCUS
    }
}

@Composable
fun RecentActivityPreview(
    sessions: List<PomodoroSession>,
    onViewAllClick: () -> Unit
) {
    ElevatedCard(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.elevatedCardColors(
            containerColor = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.5f)
        )
    ) {
        Column(
            modifier = Modifier.padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Row(
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Icon(
                        Icons.Filled.History,
                        contentDescription = null,
                        modifier = Modifier.size(20.dp),
                        tint = MaterialTheme.colorScheme.primary
                    )
                    Text(
                        "Recent Activity",
                        style = MaterialTheme.typography.titleSmall,
                        fontWeight = FontWeight.Bold
                    )
                }
                TextButton(onClick = onViewAllClick) {
                    Text("View All", style = MaterialTheme.typography.labelMedium)
                    Spacer(modifier = Modifier.width(4.dp))
                    Icon(
                        Icons.AutoMirrored.Filled.ArrowForward,
                        contentDescription = null,
                        modifier = Modifier.size(16.dp)
                    )
                }
            }
            
            HorizontalDivider(modifier = Modifier.padding(vertical = 4.dp))
            
            sessions.forEach { session ->
                RecentSessionItem(session)
            }
        }
    }
}

@Composable
fun RecentSessionItem(session: PomodoroSession) {
    val sessionType = SessionType.valueOf(session.sessionType)
    
    val sessionColor = when (sessionType) {
        SessionType.FOCUS -> MaterialTheme.colorScheme.primary
        SessionType.SHORT_BREAK -> MaterialTheme.colorScheme.secondary
        SessionType.LONG_BREAK -> MaterialTheme.colorScheme.tertiary
    }
    
    val sessionIcon = when (sessionType) {
        SessionType.FOCUS -> Icons.Filled.Timer
        SessionType.SHORT_BREAK -> Icons.Filled.Coffee
        SessionType.LONG_BREAK -> Icons.Filled.BeachAccess
    }
    
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = 4.dp),
        horizontalArrangement = Arrangement.spacedBy(12.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Side accent color bar
        Surface(
            modifier = Modifier
                .width(4.dp)
                .height(40.dp),
            color = sessionColor,
            shape = MaterialTheme.shapes.small
        ) {}
        
        // Session icon
        Surface(
            modifier = Modifier.size(40.dp),
            shape = CircleShape,
            color = sessionColor.copy(alpha = 0.15f)
        ) {
            Box(contentAlignment = Alignment.Center) {
                Icon(
                    sessionIcon,
                    contentDescription = null,
                    modifier = Modifier.size(20.dp),
                    tint = sessionColor
                )
            }
        }
        
        // Session info
        Column(modifier = Modifier.weight(1f)) {
            Text(
                session.task ?: when (sessionType) {
                    SessionType.FOCUS -> stringResource(StudyStrings.TimerSessionFocus)
                    SessionType.SHORT_BREAK -> stringResource(StudyStrings.TimerSessionShortBreak)
                    SessionType.LONG_BREAK -> stringResource(StudyStrings.TimerSessionLongBreak)
                },
                style = MaterialTheme.typography.bodyMedium,
                fontWeight = FontWeight.Medium,
                maxLines = 1
            )
            Text(
                "${session.durationMinutes}m • ${if (session.completed) "Completed" else "Interrupted"}",
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f)
            )
        }
        
        // Completion status
        if (session.completed) {
            Icon(
                Icons.Filled.CheckCircle,
                contentDescription = "Completed",
                modifier = Modifier.size(20.dp),
                tint = MaterialTheme.colorScheme.primary
            )
        }
    }
}
